import { useEffect, type ReactNode } from 'react';
import { Alert, App as AntdApp, Button, ConfigProvider, Spin } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import { useTranslation } from 'react-i18next';
import { themeConfig } from './theme';
import { SessionProvider, useSession } from './session/SessionProvider';
import { errorText } from './session/errorText';
import { useRoute, type Route } from './router';
import { PortalShell } from './shell/PortalShell';
import { EntryLayout } from './screens/EntryLayout';
import { LoginScreen } from './screens/LoginScreen';
import { RecoverScreen } from './screens/RecoverScreen';
import { PasswordScreen } from './screens/PasswordScreen';
import { DashboardScreen } from './screens/DashboardScreen';
import { AccountStateScreen } from './screens/AccountStateScreen';
import { FundUsageScreen } from './screens/FundUsageScreen';
import { PaymentsScreen } from './screens/PaymentsScreen';
import { PromisesScreen } from './screens/PromisesScreen';
import { InstallmentsScreen } from './screens/InstallmentsScreen';
import { DocumentsScreen } from './screens/DocumentsScreen';
import { PaymentScreen } from './screens/PaymentScreen';
import { InstructionsScreen } from './screens/InstructionsScreen';
import { ProfileScreen } from './screens/ProfileScreen';
import { BlockingsScreen } from './screens/BlockingsScreen';
import { DevicesScreen } from './screens/DevicesScreen';
import { AccountPasswordScreen } from './screens/AccountPasswordScreen';
import { TicketsScreen } from './screens/TicketsScreen';
import { TicketScreen } from './screens/TicketScreen';
import { NewsScreen } from './screens/NewsScreen';
import { AlertsScreen } from './screens/AlertsScreen';
import { TelevisionScreen } from './screens/TelevisionScreen';
import { TariffScreen } from './screens/TariffScreen';
import { ServicesScreen } from './screens/ServicesScreen';
import { StatisticsScreen } from './screens/StatisticsScreen';
import { ContractProvider } from './money/ContractProvider';
import { PortalConfigProvider } from './config/PortalConfigProvider';
import type { PASubscriber } from './api/pa';
import type { Navigation } from './router';

/**
 * The provider stack and the one routing decision: WHICH SCREEN, given the session state
 * and the URL.
 *
 * The table, in full, because a portal that can show a spinner for ever is the failure
 * mode this file exists to prevent:
 *
 *   loading                     → a spinner, and only while /pa/auth/me is in flight
 *   anonymous  + /recover       → the public recovery screen (a deep link from an SMS)
 *   anonymous  + anything else  → the login screen
 *   unavailable                 → what went wrong + «Повторить». NEVER a login form: a
 *                                 subscriber whose portal is down must not be told to
 *                                 try his password again.
 *   authenticated + a section   → that section inside the shell
 *   authenticated + /recover    → home, with the URL REPLACED
 *
 * An authenticated caller on /recover is sent home and the URL is REPLACED, so the
 * address bar and the highlighted section cannot disagree. He does not need the public
 * route — he is holding a session, and §5 is the screen that changes a password from
 * inside one.
 *
 * ⚠ THE MONEY SCREENS SIT INSIDE `ContractProvider`, WHICH SITS INSIDE THE AUTHENTICATED
 * BRANCH. Two consequences, both deliberate: the contract list is read ONCE for the whole
 * session rather than per screen (so moving from the dashboard to the payment history cannot
 * silently switch contract), and no money request is ever issued by an anonymous page — the
 * provider does not exist until there is a session to make it with.
 */
function Routed() {
  const { t } = useTranslation();
  const { state, retry } = useSession();
  const { route, navigate } = useRoute();

  const authenticated = state.status === 'authenticated';
  useEffect(() => {
    if (authenticated && route.kind === 'recover') {
      navigate({ kind: 'home' }, { replace: true });
    }
  }, [authenticated, route.kind, navigate]);

  if (state.status === 'loading') {
    return (
      <div className="pa-entry">
        <Spin size="large" tip={t('common.loading')} fullscreen={false}>
          {/* Spin needs a child to attach its tip to in AntD 5. */}
          <div style={{ width: 1, height: 40 }} />
        </Spin>
      </div>
    );
  }

  if (state.status === 'unavailable') {
    return (
      <EntryLayout>
        <Alert
          type="error"
          showIcon
          message={t('common.unavailableTitle')}
          description={errorText(state.error, t)}
          style={{ marginBottom: 20 }}
        />
        <Button type="primary" size="large" block onClick={retry}>
          {t('common.retry')}
        </Button>
      </EntryLayout>
    );
  }

  if (state.status === 'anonymous') {
    return route.kind === 'recover' ? (
      <RecoverScreen navigate={navigate} />
    ) : (
      <LoginScreen navigate={navigate} />
    );
  }

  return (
    <ContractProvider>
      <PortalShell subscriber={state.subscriber} route={route} navigate={navigate}>
        <Section
          route={route}
          subscriber={state.subscriber}
          expiresAt={state.expiresAt}
          navigate={navigate}
        />
      </PortalShell>
    </ContractProvider>
  );
}

/**
 * Section is the route → screen table for an authenticated caller.
 *
 * It is a `switch` over the SAME `RouteKind` union `nav.ts` derives its keys from, and its
 * `default` arm assigns the narrowed `never` — so a route added to `router.ts` without a
 * screen is a COMPILE error rather than a blank page inside the shell. See the arm itself for
 * why the union alone is not enough (`undefined` is a valid `ReactNode`).
 */
function Section({
  route,
  subscriber,
  expiresAt,
  navigate,
}: {
  route: Route;
  subscriber: PASubscriber;
  expiresAt: string;
  navigate: Navigation['navigate'];
}): ReactNode {
  switch (route.kind) {
    case 'password':
      return <PasswordScreen />;
    case 'topup':
      return <PaymentScreen navigate={navigate} />;
    case 'instructions':
      return <InstructionsScreen />;
    case 'state':
      return <AccountStateScreen />;
    case 'usage':
      return <FundUsageScreen />;
    case 'payments':
      return <PaymentsScreen />;
    case 'promises':
      return <PromisesScreen />;
    case 'installments':
      return <InstallmentsScreen />;
    case 'documents':
      return <DocumentsScreen />;
    // W4 — the self-service sections. `profile` needs no contract (the contacts are the
    // SUBSCRIBER's); the other three are contract-scoped and reach it through `MoneyPage`,
    // which is what keeps «which contract am I looking at» answered once.
    case 'profile':
      return <ProfileScreen />;
    case 'blockings':
      return <BlockingsScreen />;
    case 'devices':
      return <DevicesScreen />;
    case 'accountPassword':
      return <AccountPasswordScreen />;
    // P2 — the four sections that are neither money nor identity. `tickets` and `news` are
    // SUBSCRIBER-scoped (a ticket hangs off `ticket.subscriber_id`, an announcement off its
    // audience), so neither goes through `MoneyPage`; `tv` is contract-scoped and does.
    case 'tickets':
      return <TicketsScreen navigate={navigate} />;
    case 'ticket':
      // ⚠ THE ONE PARAMETRISED ROUTE. A `ticket` with no id cannot happen through `parseRoute`
      // (it only produces one for `/tickets/<digits>`), but the TYPE admits it — so the list
      // is what renders rather than a thread screen fetching `/pa/tickets/undefined`, which
      // the server would answer 404 for a reason no screen could explain.
      return route.id === undefined ? (
        <TicketsScreen navigate={navigate} />
      ) : (
        <TicketScreen id={route.id} navigate={navigate} />
      );
    case 'news':
      return <NewsScreen />;
    case 'alerts':
      return <AlertsScreen />;
    case 'tv':
      return <TelevisionScreen />;
    // P3 — the last three self-service sections. All three are ACCOUNT-scoped (their routes
    // take the Account in the path), so each reaches its account through `AccountScope`, which
    // reads the same dashboard list the landing page draws its cards from.
    case 'tariff':
      return <TariffScreen />;
    case 'services':
      return <ServicesScreen />;
    case 'statistics':
      return <StatisticsScreen />;
    case 'home':
    case 'recover':
      // `recover` is corrected to home by the effect above; rendering the dashboard in the
      // one frame before that lands is what keeps the transition free of a flash.
      return (
        <DashboardScreen subscriber={subscriber} expiresAt={expiresAt} navigate={navigate} />
      );
    default:
      // ⚠ THE EXHAUSTIVENESS CHECK, AND IT IS NOT DECORATION. Without it a `RouteKind` added
      // to `router.ts` with no case here compiles CLEANLY — `undefined` is a member of
      // `ReactNode`, so the switch simply falls off the end and the new section renders a
      // blank page inside the shell. The first version of this file claimed the union alone
      // made that a type error; it does not, and the claim was checked by adding a tenth kind
      // and watching `tsc` pass. Assigning the narrowed `never` is what turns it into the
      // error the comment promised.
      return assertNoUnhandledRoute(route.kind);
  }
}

/** assertNoUnhandledRoute fails to COMPILE when a route kind has no screen. */
function assertNoUnhandledRoute(kind: never): never {
  throw new Error(`portal: no screen for route ${String(kind)}`);
}

/**
 * ⚠ `PortalConfigProvider` SITS ABOVE `SessionProvider`, and that is load-bearing rather
 * than tidy: `GET /pa/config` is the listener's one PUBLIC read and the LOGIN SCREEN
 * renders its welcome text, its terms link and its footer — so it must resolve for a
 * caller who has no session and may never get one. It never blocks and never fails: every
 * field defaults empty, an empty field renders nothing, and a failed read is
 * indistinguishable from an operator who has written nothing.
 */
export function App() {
  return (
    <ConfigProvider theme={themeConfig} locale={ruRU}>
      <AntdApp>
        <PortalConfigProvider>
          <SessionProvider>
            <Routed />
          </SessionProvider>
        </PortalConfigProvider>
      </AntdApp>
    </ConfigProvider>
  );
}
